import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { apiService } from '../services/api';
import previewI18n from '../preview/i18n';
import { syncPreviewThemeFromProfile, setPreviewTheme } from '../preview/theme/previewTheme';
import type { UserProfile, LoginRequest, RegisterRequest, Permission } from '../types/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  userId: string | null;
  cardId: string | null;
  username: string | null;
  userProfile: UserProfile | null;
  permission: Permission | null;
  hasAdminAccess: boolean; // ADVANCED 또는 ADMIN 권한 확인
  isMonitor: boolean;
  konasuteEnable: boolean; // 코나스테 사용 여부
  konasuteHookGranted: boolean; // 어드민이 부여한 코나스테 훅(클라이언트) 사용 권한
  isInit: boolean; // 온보딩 완료 여부 (default true to avoid flash)
  isInitLoaded: boolean; // profile fetched at least once (gate the onboarding modal on this)
  language: string | null; // 사용자가 선택한 UI 언어. null이면 첫 진입 게이트 모달 노출.
  loading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  handleAuthError: () => void;
  updateUsername: (newUsername: string) => void;
  updateKonasuteEnable: (enable: boolean) => void;
  setIsInit: (v: boolean) => void;
  setLanguage: (lng: string) => Promise<void>;
  refreshUserProfile: (version: '6' | '7') => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null); // cardId
  const [cardId, setCardId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [permission, setPermission] = useState<Permission | null>(null);
  const [konasuteEnable, setKonasuteEnable] = useState(false);
  const [konasuteHookGranted, setKonasuteHookGranted] = useState(false);
  const [isInit, setIsInit] = useState(true); // default true to avoid flash; set from profile on load
  // Tracks whether profile fetch has completed at least once. Layouts gate the
  // onboarding modal on this so we don't flash a wrong state. Stale tabs that
  // never re-mount the provider keep this false → no modal until reload.
  const [isInitLoaded, setIsInitLoaded] = useState(false);
  // Server-stored UI language. null until profile loads. The LanguageGate
  // modal forces a selection on first login.
  const [language, setLanguageState] = useState<string | null>(null);

  // Sync the i18n instance whenever the server-stored preference changes —
  // covers both fresh login (profile arrives async) and cross-device sync
  // when refreshProfile re-fetches.
  useEffect(() => {
    if (!language) return;
    if (previewI18n.language !== language) {
      previewI18n.changeLanguage(language);
    }
  }, [language]);
  const [loading, setLoading] = useState(true);

  // ADVANCED 또는 ADMIN 권한 확인
  const hasAdminAccess = permission === 'ADVANCED' || permission === 'ADMIN';
  const isMonitor = permission === 'MONITOR';

  // Check for saved session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedCardId = localStorage.getItem('userId'); // cardId가 userId로 저장됨
    const savedUsername = localStorage.getItem('username');
    const savedDisplayName = localStorage.getItem('displayName');
    const savedPlayCount = localStorage.getItem('playCount');
    const savedSkillLevel = localStorage.getItem('skillLevel');
    const savedVolforce = localStorage.getItem('volforce');
    const savedPermission = localStorage.getItem('permission') as Permission | null;
    const savedKonasuteEnable = localStorage.getItem('konasuteEnable') === 'true';
    const savedKonasuteHookGranted = localStorage.getItem('konasuteHookGranted') === 'true';

    if (savedToken && savedCardId) {
      setUserId(savedCardId);
      setCardId(savedCardId);
      setUsername(savedUsername);
      setPermission(savedPermission);
      setKonasuteEnable(savedKonasuteEnable);
      setKonasuteHookGranted(savedKonasuteHookGranted);
      setUserProfile({
        displayName: savedDisplayName || savedUsername || savedCardId,
        userId: savedCardId,
        playCount: savedPlayCount ? parseInt(savedPlayCount, 10) : 0,
        skillLevel: savedSkillLevel || '',
        volforce: savedVolforce ? parseFloat(savedVolforce) : 0,
        permission: savedPermission || undefined,
        konasuteEnable: savedKonasuteEnable
      });
      setIsAuthenticated(true);
      setLoading(false);

      // Fetch fresh profile to hydrate isInit (default useState(true) hides modal otherwise)
      const savedVersion = (localStorage.getItem('gameVersion') === '6' ? '6' : '7') as '6' | '7';
      apiService.getProfile(savedVersion).then(profile => {
        setKonasuteHookGranted(profile.konasuteHookGranted ?? false);
        localStorage.setItem('konasuteHookGranted', String(profile.konasuteHookGranted ?? false));
        setIsInit(profile.isInit ?? false);
        setLanguageState(profile.language ?? null);
        syncPreviewThemeFromProfile(profile.profileTheme);
      }).catch((e) => {
        console.error('Profile hydrate (saved-session) failed:', e);
      }).finally(() => setIsInitLoaded(true));
    } else {
      // 토큰이 없으면 인증 데이터 정리
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      localStorage.removeItem('displayName');
      localStorage.removeItem('playCount');
      localStorage.removeItem('skillLevel');
      localStorage.removeItem('volforce');
      localStorage.removeItem('permission');
      localStorage.removeItem('konasuteEnable');
      localStorage.removeItem('konasuteHookGranted');
      setLoading(false);
    }
  }, []);

  const register = async (data: RegisterRequest) => {
    try {
      setLoading(true);
      const response = await apiService.register(data);

      if (response.success === 'true') {
        // 회원가입 성공
        return;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const login = async (data: LoginRequest) => {
    try {
      setLoading(true);
      const response = await apiService.login(data);

      if (response.success && response.cardId && response.token) {
        const displayName = response.displayName || response.username || response.cardId;
        const konasuteEnabled = response.konasuteEnable || false;

        // Save to state
        setUserId(response.cardId);
        setCardId(response.cardId);
        setUsername(response.username || null);
        setPermission(response.permission || null);
        setKonasuteEnable(konasuteEnabled);
        setUserProfile({
          displayName: displayName,
          userId: response.cardId,
          playCount: response.playCount || 0,
          skillLevel: response.skillLevel || '',
          volforce: response.volforce || 0,
          permission: response.permission,
          konasuteEnable: konasuteEnabled
        });
        setIsAuthenticated(true);

        // Save to localStorage
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('userId', response.cardId);
        localStorage.setItem('username', response.username || '');
        localStorage.setItem('displayName', displayName);
        localStorage.setItem('playCount', String(response.playCount || 0));
        localStorage.setItem('skillLevel', response.skillLevel || '');
        localStorage.setItem('volforce', String(response.volforce || 0));
        localStorage.setItem('konasuteEnable', String(konasuteEnabled));
        if (response.permission) {
          localStorage.setItem('permission', response.permission);
        }

        // Fetch profile to hydrate isInit — login response doesn't include it.
        // Without this, fresh logins miss the onboarding gate (isInit default = true).
        try {
          const profile = await apiService.getProfile('7');
          setKonasuteHookGranted(profile.konasuteHookGranted ?? false);
          localStorage.setItem('konasuteHookGranted', String(profile.konasuteHookGranted ?? false));
          setIsInit(profile.isInit ?? false);
          setLanguageState(profile.language ?? null);
          syncPreviewThemeFromProfile(profile.profileTheme);
        } catch (e) {
          console.error('Failed to fetch profile after login for isInit:', e);
        } finally {
          setIsInitLoaded(true);
        }
      } else {
        throw new Error(response.message || '로그인 실패');
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUserId(null);
    setCardId(null);
    setUsername(null);
    setUserProfile(null);
    setPermission(null);
    setKonasuteEnable(false);
    setKonasuteHookGranted(false);
    setIsAuthenticated(false);

    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('displayName');
    localStorage.removeItem('playCount');
    localStorage.removeItem('skillLevel');
    localStorage.removeItem('volforce');
    localStorage.removeItem('permission');
    localStorage.removeItem('konasuteEnable');
    localStorage.removeItem('konasuteHookGranted');

    // 장착 테마는 계정 소유물 — 로그아웃 시 기본 테마로 복귀
    setPreviewTheme('default');
  };

  const handleAuthError = () => {
    logout();
    // Additional error handling can be added here if needed
  };

  const updateUsername = (newUsername: string) => {
    setUsername(newUsername);
    localStorage.setItem('username', newUsername);
    // userProfile도 업데이트
    setUserProfile(prev => prev ? {
      ...prev,
      displayName: newUsername
    } : null);
  };

  const updateKonasuteEnable = (enable: boolean) => {
    setKonasuteEnable(enable);
    localStorage.setItem('konasuteEnable', String(enable));
    // userProfile도 업데이트
    setUserProfile(prev => prev ? {
      ...prev,
      konasuteEnable: enable
    } : null);
  };

  const refreshUserProfile = async (version: '6' | '7') => {
    if (!isAuthenticated) return;

    try {
      const profile = await apiService.getProfile(version);
      setPermission(profile.permission);
      setKonasuteEnable(profile.konasuteEnable);
      setKonasuteHookGranted(profile.konasuteHookGranted ?? false);
      syncPreviewThemeFromProfile(profile.profileTheme);
      // isInit: missing field → false (user needs onboarding) per spec §1.7
      setIsInit(profile.isInit ?? false);
      setIsInitLoaded(true);
      setUserProfile({
        displayName: profile.displayName,
        userId: profile.cardId,
        playCount: profile.playCount,
        skillLevel: profile.skillLevel,
        volforce: profile.volforce,
        permission: profile.permission,
        konasuteEnable: profile.konasuteEnable
      });
      // localStorage 업데이트
      localStorage.setItem('displayName', profile.displayName);
      localStorage.setItem('playCount', String(profile.playCount));
      localStorage.setItem('skillLevel', profile.skillLevel);
      localStorage.setItem('volforce', String(profile.volforce));
      localStorage.setItem('permission', profile.permission);
      localStorage.setItem('konasuteEnable', String(profile.konasuteEnable));
      localStorage.setItem('konasuteHookGranted', String(profile.konasuteHookGranted ?? false));
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  };

  // Lightweight re-fetch for post-onboarding state sync (uses current version)
  const refreshProfile = async () => {
    if (!isAuthenticated) return;
    try {
      const profile = await apiService.getProfile();
      setPermission(profile.permission);
      setKonasuteEnable(profile.konasuteEnable);
      setKonasuteHookGranted(profile.konasuteHookGranted ?? false);
      syncPreviewThemeFromProfile(profile.profileTheme);
      setIsInit(profile.isInit ?? false);
      setLanguageState(profile.language ?? null);
      setIsInitLoaded(true);
      setUserProfile({
        displayName: profile.displayName,
        userId: profile.cardId,
        playCount: profile.playCount,
        skillLevel: profile.skillLevel,
        volforce: profile.volforce,
        permission: profile.permission,
        konasuteEnable: profile.konasuteEnable
      });
      localStorage.setItem('displayName', profile.displayName);
      localStorage.setItem('playCount', String(profile.playCount));
      localStorage.setItem('skillLevel', profile.skillLevel);
      localStorage.setItem('volforce', String(profile.volforce));
      localStorage.setItem('permission', profile.permission);
      localStorage.setItem('konasuteEnable', String(profile.konasuteEnable));
      localStorage.setItem('konasuteHookGranted', String(profile.konasuteHookGranted ?? false));
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  };

  const setLanguage = async (lng: string) => {
    if (!isAuthenticated) return;
    setLanguageState(lng);
    try {
      await apiService.updateLanguage(lng);
    } catch (e) {
      console.error('Failed to persist language to server:', e);
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userId,
      cardId,
      username,
      userProfile,
      permission,
      hasAdminAccess,
      isMonitor,
      konasuteEnable,
      konasuteHookGranted,
      isInit,
      isInitLoaded,
      language,
      loading,
      login,
      register,
      logout,
      handleAuthError,
      updateUsername,
      updateKonasuteEnable,
      setIsInit,
      setLanguage,
      refreshUserProfile,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};